import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import MetaControl from 'Components/UI/Meta/MetaControl';
import ValidatedControl from 'Components/UI/Meta/MetaControl/ValidatedControl';
import { makeStyles } from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/styles';
import { dataAttrs, testAttr } from 'Helpers/Testing';
import useMetaLayoutContext from './MetaLayoutContext';

const useStyles = makeStyles(theme => ({
  root: {},
  rootLegacy: {
    display: 'flex',
    flex: '1',
    justifyContent: 'flex-start',
    width: '100%',
    marginBottom: theme.spacing(1),
    '& .MuiFormHelperText-root': {
      position: 'relative',
    },
    '& .MuiFormHelperText-root > span': {
      fontSize: theme.typography.h6.fontSize,
    },
    '& > .MuiFormLabel-root': {
      whiteSpace: 'nowrap',
      maxWidth: '100%',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },
  },
  rootHidden: {
    // Using !important because other components are overriding this and causing hidden items to appear
    // Do not copy this elsewhere
    display: 'none !important',
  },
}));

function MetaControlField({
  attr,
  notInLayout,
  className,
  onChange,
  hideViaMetaOverride,
  simpleRootStyle,
  onInlineSubmit: onInlineSubmitProp, // Override the form default submit behaviour
  alwaysDirty, // You may you want to use this when using onInlineSubmit when you want to be able to click the same option more than once and submit to server
  allowedMetaProps,
  ...rest
}) {
  const {
    onSubmit,
    handleChange,
    getError,
    resource,
    remainingFields,
    markFieldExplicitlyRendered,
    inlineSubmit,
    saving,
    onAdd,
    onRemove,
    metaOverrideSet,
    applyMetaOverrideSet,
    autoSave,
  } = useMetaLayoutContext();
  const controlClasses = useStyles();

  const template = (applyMetaOverrideSet) ? resource.metaField(attr, { metaOverrideSet }) : resource.meta().field(attr);

  // Do not render if the attr is not on the template
  if (!template || template.isEmpty()) {
    return null;
  }

  const attrName = template.attr('name');

  const error = getError(attrName);

  const errorProps = {};
  if (error) {
    errorProps.error = true;
    errorProps.helperText = error.shortDetail || error.detail;
  }

  const layoutSpecificallyRenderingField = !notInLayout;
  if (layoutSpecificallyRenderingField && remainingFields.has(attrName)) {
    markFieldExplicitlyRendered(attrName);
  }

  let onInlineSubmit = null;
  if (onInlineSubmitProp) {
    onInlineSubmit = onInlineSubmitProp;
  } else if (inlineSubmit) {
    onInlineSubmit = onSubmit;
  }

  const Control = onInlineSubmit ? ValidatedControl : MetaControl;

  return (
    <Control
      {...dataAttrs('meta-control', resource, {
        [testAttr.dataAttrName]: attrName,
      })}
      key={attrName}
      onInlineSubmit={onInlineSubmit}
      className={classNames(controlClasses.root, className, {
        [controlClasses.rootLegacy]: !simpleRootStyle,
        [controlClasses.rootHidden]:
          rest.hidden
          || (hideViaMetaOverride && template.attr('hidden'))
          || (rest.hideEmptyFields && resource.isAttrEmpty(attrName)),
      })}
      attrName={attrName || attr}
      onChange={(v) => {
        let cleanedValue = v;
        if (template.attr('_formatValue')) {
          const formatter = template.attr('_formatValue');
          cleanedValue = formatter(v);
        }
        handleChange(attrName, cleanedValue);
        if (onChange) {
          onChange(cleanedValue);
        }
      }}
      metaOverrideSet={metaOverrideSet}
      resource={resource}
      title={template.attr('prompt')}
      onAdd={onAdd}
      onRemove={onRemove}
      disabled={autoSave ? false : saving}
      alwaysDirty={alwaysDirty}
      // This is added with allowedMetaProps to make this only an additive change. There are fields with description set that
      // would unintentionally break if this was just blindly set without forcing a whitelist to allow the prop.
      description={allowedMetaProps.includes('description') ? template.attr('description') : undefined}
      {...errorProps}
      {...rest}
    />
  );
}

MetaControlField.propTypes = {
  attr: PropTypes.string.isRequired,
  notInLayout: PropTypes.bool,
  alwaysDirty: PropTypes.bool,
  className: PropTypes.string,
  hideViaMetaOverride: PropTypes.bool,
  simpleRootStyle: PropTypes.bool,
  allowedMetaProps: PropTypes.arrayOf(PropTypes.string),
};

MetaControlField.defaultProps = {
  className: '',
  notInLayout: false,
  simpleRootStyle: false,
  alwaysDirty: false,
  hideViaMetaOverride: false, // This should be true but is not in order to avoid unintended consequences
  allowedMetaProps: [],
};

export default MetaControlField;
